<script setup="">
import { AlertCircleIcon, ChevronDownIcon, EyeIcon, PenIcon, ShieldIcon, StarIcon, Undo2Icon } from "lucide-vue-next";
import { computed, onMounted, watch } from "vue";

import CampaignMenuList from "@/Layouts/Partials/CampaignMenuList.vue";
import TeamProfile from "@/Layouts/Partials/TeamProfile.vue";
import UserMenuList from "@/Layouts/Partials/UserMenuList.vue";
import UserProfile from "@/Layouts/Partials/UserProfile.vue";

import ApplicationLogo from "@/Components/ApplicationLogo.vue";
import ApplicationTextLogo from "@/Components/ApplicationTextLogo.vue";

import useSidebar from "@/composables/sidebar";
import useWebPush from "@/composables/webpush";
import { useResponsiveStore } from "@/stores/responsive";
import { Link, usePage } from "@inertiajs/vue3";
import { useMediaQuery } from "@vueuse/core";

const isStandalone = useMediaQuery("(display-mode: standalone)");
const isFullscreen = useMediaQuery("(display-mode: fullscreen)");
const isMinimalUi = useMediaQuery("(display-mode: minimal-ui)");

const page = usePage();
const sidebar = useSidebar();
const webPush = useWebPush();
const responsive = useResponsiveStore();

const team = computed(() => page.props.team);
const isShowMenu = computed(() => !!page.props.team.membership);

watch(
  () => responsive.lgAndDown,
  (value) => {
    if (sidebar.isAutoFold.value) {
      if (!value && sidebar.isSecondaryOpen.value) {
        sidebar.setPrimaryMinimized(true);
        return;
      }

      sidebar.setPrimaryMinimized(value);
    }
  }
);

const teamRingColor = (planName) => {
  switch (planName) {
    case "pro":
      return "ring-blue-700 dark:ring-blue-500";
    case "team":
      return "ring-purple-700 dark:ring-purple-500";
    default:
      return "ring-700";
  }
};

onMounted(() => {
  if (
    isStandalone.value ||
    isFullscreen.value ||
    isMinimalUi.value ||
    window?.navigator?.standalone ||
    document.referrer?.includes("android-app://")
  ) {
    if (webPush.isAvailable()) {
      webPush.getPushSubscription().then((subscription) => {
        if (!subscription) {
          webPush.enablePushNotification();
        }
      });
    }
  }
});
</script>

<template>
  <nav class="app-header primary-sidebar">
    <div class="primary-sidebar-content flex h-full grow flex-col py-2">
      <div class="flex-center h-16 px-1">
        <Link :href="route('home')" class="text-brand dark:text-rose-500">
          <ApplicationLogo
            v-if="sidebar.isPrimaryMinimized.value"
            class="mx-auto w-auto transition-transform duration-500 ease-in-out hover:rotate-[360deg]"
          />
          <ApplicationTextLogo v-else classes="w-16" />
        </Link>
      </div>
      <!--end::sidebar-panel-header-->

      <div class="relative flex flex-1 flex-col justify-between overflow-y-auto scrollbar-hide">
        <div class="relative space-y-4" :class="[sidebar.isPrimaryMinimized.value ? 'px-2' : 'px-4']">
          <div>
            <div :class="[sidebar.isPrimaryMinimized.value ? 'py-2' : 'rounded-xl border bg-white p-2']">
              <TeamProfile :panel-class="sidebar.isPrimaryMinimized ? 'ml-2 mt-0' : 'ml-4 mt-0'">
                <template #default="{ open }">
                  <div
                    class="flex h-16 w-full items-center rounded-xl ring-0 transition duration-300 hover:bg-gray-100 focus:outline-none dark:hover:bg-gray-700"
                    :class="[sidebar.isPrimaryMinimized.value ? 'justify-center px-1.5' : 'justify-between px-4']"
                  >
                    <div class="relative">
                      <img
                        :src="team.campaign.profile_photo_url"
                        :class="teamRingColor(team.subscription.plan.name)"
                        alt="Team Profile Photo"
                        class="relative z-0 h-10 w-10 flex-none overflow-hidden rounded-full object-cover p-px ring-2 dark:ring-zinc-500"
                      />

                      <div class="flex-center absolute -right-1 bottom-0 z-1 h-4 w-4 rounded-full text-white">
                        <span v-if="team.membership.role.key === 'owner'" v-tippy="{ content: '캠페인 소유자 입니다.' }"
                          ><StarIcon class="h-4 w-4 fill-primary"
                        /></span>
                        <span v-if="team.membership.role.key === 'admin'" v-tippy="{ content: '캠페인 관리자 입니다.' }"
                          ><ShieldIcon class="h-4 w-4 fill-primary"
                        /></span>
                        <span v-if="team.membership.role.key === 'editor'" v-tippy="{ content: '캠페인 편집자 입니다.' }"
                          ><PenIcon class="h-4 w-4 fill-primary"
                        /></span>
                        <span v-if="team.membership.role.key === 'guest'" v-tippy="{ content: '캠페인 게스트 입니다.' }"
                          ><EyeIcon class="h-4 w-4 fill-primary"
                        /></span>
                      </div>
                    </div>
                    <div class="expanded ml-4">
                      <div class="flex flex-1 items-center justify-end gap-x-2 text-900">
                        <h1 class="expanded w-24 truncate text-left text-sm+ font-bold">{{ team.campaign.name }}</h1>
                        <ChevronDownIcon class="expanded h-4 w-4 transition" :class="[open ? '-rotate-90' : '']" />
                      </div>
                      <div class="expanded relative flex items-stretch gap-x-2">
                        <p
                          class="h-4 flex-none text-left text-xs font-semibold first-letter:uppercase"
                          :class="{
                            'text-600': team.subscription.plan.name === 'free',
                            'text-info': team.subscription.plan.name === 'pro',
                            'text-warning': team.subscription.plan.name === 'team'
                          }"
                        >
                          {{ team.subscription.plan.name }} plan
                        </p>
                        <div class="mr-2 h-2 flex-1 rounded-br-md border-b border-r border-300"></div>
                      </div>
                    </div>
                  </div>
                </template>
              </TeamProfile>
              <!--end::app-profile-->

              <div v-if="!isShowMenu" class="space-y-1 p-2">
                <Link
                  v-tippy="{ content: '내 캠페인으로 이동합니다.', placement: 'right' }"
                  :href="route('app.campaigns.index', team.name)"
                  class="mx-auto flex h-11 w-11 items-center justify-center rounded-full border bg-white transition-colors text-600 hover:text-800 hover:border-400"
                  :class="[sidebar.isPrimaryMinimized.value ? 'px-2' : 'px-3']"
                >
                  <Undo2Icon class="h-5 w-5" />
                  <span v-if="!sidebar.isPrimaryMinimized.value" class="ml-2 text-sm font-medium">홈으로</span>
                </Link>
              </div>
              <!--end::buttons-->
            </div>
          </div>

          <CampaignMenuList v-if="isShowMenu" :team="team" />
          <!--end::main-menu-list-->
        </div>
        <!--end::sidebar-panel-body-->

        <div class="mt-10 px-2">
          <UserMenuList />

          <div class="h-18 py-2">
            <UserProfile panel-class="bottom-18">
              <template #default="{ user }">
                <div class="flex-center w-full rounded-xl px-4 py-2 hover:bg-100">
                  <div class="avatar h-9 w-9">
                    <img :src="user.profile_photo_url" alt="avatar" class="rounded-full p-px ring-2 ring-400" />
                  </div>

                  <div class="flex flex-1 items-center">
                    <div class="expanded ml-3 flex-1 text-left">
                      <a class="line-clamp-1 truncate whitespace-nowrap text-sm+ font-semibold dark:text-gray-100">
                        {{ user.name }}
                      </a>
                      <p class="-mt-0.5 line-clamp-1 truncate whitespace-nowrap text-xs+ text-400">
                        {{ user.email }}
                      </p>
                    </div>
                    <Link
                      v-if="!user.is_verified"
                      v-tippy="{ content: '미인증 계정입니다. 인증을 진행해주세요.' }"
                      :href="route('accounts.show')"
                      class="hover:text-warning-600 expanded ml-2 flex-none text-warning transition-colors"
                    >
                      <AlertCircleIcon class="h-6 w-6 animate-pulse" />
                    </Link>
                  </div>
                </div>
              </template>
              <!--end::profile-->
            </UserProfile>
          </div>
        </div>
        <!--end::sidebar-panel-footer-->
      </div>
    </div>
  </nav>
</template>
